import React from 'react';

interface PriceBreakdownProps {
  basePrice: number;
  spellcheckAdded: boolean;
  coverDesignAdded: boolean;
  spellcheckPrice: number;
  coverDesignPrice: number;
  cgst: number;
  sgst: number;
  totalPrice: number;
  isProcessing: boolean;
  isRemovingAddon: boolean;
  onRemoveSpellcheck: () => void;
  onRemoveCoverDesign: () => void;
}

const PriceBreakdown: React.FC<PriceBreakdownProps> = ({
  spellcheckAdded, coverDesignAdded, spellcheckPrice, coverDesignPrice,
  cgst, sgst, totalPrice, isProcessing, isRemovingAddon, onRemoveSpellcheck, onRemoveCoverDesign
}) => {
  const hasAddons = spellcheckAdded || coverDesignAdded;

  return (
    <div className="space-y-4">

      {/* Top platform positioning */}
      <div className="bg-green-50 border border-green-200 rounded-lg px-5 py-4">
        <p className="text-green-800 text-sm font-semibold mb-1">IndiePress is free to use.</p>
        <p className="text-green-700 text-sm">No payment required now — not for the tool, not for any add-ons.</p>
      </div>

      {/* Price breakdown card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">

        {/* Base — Publishing Tool */}
        <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-100">
          <div>
            <p className="text-gray-900 text-sm font-medium">Publishing Tool</p>
            <p className="text-gray-500 text-xs mt-0.5">Formatting, typesetting & review</p>
          </div>
          <span className="text-green-600 text-base font-semibold bg-green-50 px-3 py-1 rounded-full border border-green-200">Free</span>
        </div>

        {/* Add-ons section */}
        {hasAddons && (
          <div className="divide-y divide-gray-100">
            <div className="px-6 py-2">
              <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Optional Add-ons</p>
            </div>

            {spellcheckAdded && (
              <div className="flex justify-between items-center px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-md flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-900 text-sm font-medium">Spellcheck</p>
                    <button onClick={onRemoveSpellcheck} disabled={isProcessing} className="text-orange-500 hover:text-orange-600 text-xs disabled:opacity-50">
                      {isRemovingAddon ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
                <span className="text-gray-900 text-sm font-medium">₹ {spellcheckPrice}</span>
              </div>
            )}

            {coverDesignAdded && (
              <div className="flex justify-between items-center px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-orange-50 rounded-md flex items-center justify-center flex-shrink-0">
                    <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-gray-900 text-sm font-medium">Book Cover Design</p>
                    <button onClick={onRemoveCoverDesign} disabled={isProcessing} className="text-orange-500 hover:text-orange-600 text-xs disabled:opacity-50">
                      {isRemovingAddon ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
                <span className="text-gray-900 text-sm font-medium">₹ {coverDesignPrice}</span>
              </div>
            )}
          </div>
        )}

        {/* GST — only when add-ons present */}
        {hasAddons && (
          <div className="bg-gray-50 border-t border-gray-100 px-6 py-3 space-y-1">
            <div className="flex justify-between text-sm text-gray-500"><span>CGST</span><span>₹ {cgst}</span></div>
            <div className="flex justify-between text-sm text-gray-500"><span>SGST</span><span>₹ {sgst}</span></div>
          </div>
        )}

        {/* Total */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-gray-200">
          <span className="text-gray-900 text-base font-semibold">Total</span>
          {totalPrice === 0
            ? <span className="text-green-600 text-xl font-bold">₹ 0</span>
            : <span className="text-gray-900 text-xl font-bold">₹ {totalPrice}</span>
          }
        </div>
      </div>

      {/* Submit reassurance */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-5 py-4">
        <p className="text-gray-700 text-sm">Submit your book for free. Our team will review your submission and reach out within 48 hours with next steps.</p>
      </div>

    </div>
  );
};

export default PriceBreakdown;
